import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../Header";
import Footer from "../Footer";
import useSeo from "../../hooks/useSeo";
import { shopGet } from "../../api/shopApi";
import ShopProductCard from "./ShopProductCard";
import GoodsChannelTabs from "./GoodsChannelTabs";

export default function ShopHomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "生活好物商城 - 精选日常用品",
    metaDescription: "精选日常生活用品，查看商品详情与购买信息。",
    path: "/shop",
    keywords: "生活好物,日用品,口腔护理,洗发护发",
  });

  useEffect(() => {
    setLoading(true);
    shopGet("/home")
      .then(setData)
      .catch((e) => setError(e.message || "加载失败"))
      .finally(() => setLoading(false));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/shop?q=${encodeURIComponent(q)}` : "/shop");
    if (q) {
      setLoading(true);
      shopGet(`/products?keyword=${encodeURIComponent(q)}&page_size=24`)
        .then((res) => setData((prev) => ({ ...(prev || {}), searchItems: res.items || [], searchTotal: res.total })))
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  };

  const searchItems = data?.searchItems;
  const list = searchItems || data?.latest || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-8">
        <div className="max-w-6xl mx-auto space-y-8">
          <header className="text-center space-y-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">生活好物</h1>
            <p className="text-gray-500 text-sm md:text-base">
              {data?.subtitle || "精选日常好物，让生活更简单。"}
            </p>
            <GoodsChannelTabs />
          </header>

          <form onSubmit={onSearch} className="max-w-xl mx-auto flex gap-2">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索商品名称、品牌…"
              className="flex-1 border border-pink-100 rounded-full px-4 py-2.5 bg-white text-sm"
            />
            <button type="submit" className="px-5 py-2.5 rounded-full bg-pink-500 text-white text-sm">
              搜索
            </button>
          </form>

          {data?.categories?.length ? (
            <nav className="flex flex-wrap justify-center gap-2">
              {data.categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/shop/category/${c.slug}`}
                  className="px-4 py-1.5 rounded-full bg-white border border-pink-100 text-sm text-pink-700 hover:bg-pink-50"
                >
                  {c.name}
                </Link>
              ))}
            </nav>
          ) : null}

          {error ? <p className="text-center text-rose-600 text-sm">{error}</p> : null}
          {loading ? <p className="text-center text-gray-400 py-10">加载中…</p> : null}

          {!loading && searchItems ? (
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">搜索结果（{data.searchTotal || 0}）</h2>
              {list.length === 0 ? (
                <p className="text-center text-gray-400 py-8">没有找到相关商品</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {list.map((item) => (
                    <ShopProductCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </section>
          ) : null}

          {!loading && !searchItems && data?.featured?.length ? (
            <section className="space-y-4">
              <h2 className="text-xl font-bold text-gray-800">精选好物</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {data.featured.map((item) => (
                  <ShopProductCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ) : null}

          {!loading && !searchItems ? (
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800">最新上架</h2>
                <Link to="/shop/orders" className="text-sm text-pink-600 hover:underline">
                  我的订单
                </Link>
              </div>
              {list.length === 0 ? (
                <p className="text-center text-gray-400 py-10">暂无上架商品，请稍后再来看看</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {list.map((item) => (
                    <ShopProductCard key={item.id} item={item} />
                  ))}
                </div>
              )}
            </section>
          ) : null}

          <p className="text-center text-xs text-gray-400 leading-relaxed max-w-2xl mx-auto">
            {data?.shipping_note ||
              "当前为人工确认订单，暂不在线支付。提交后平台将核实商品供应与发货安排。"}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
